import { defineStore } from 'pinia';
import Auth from '@/services/auth';
import { normalizeError } from '@/utils/error-handler';
import { Apii } from '@/services/api.js';
import { generateUUID } from '@/utils/uuid.js';

const ApiService = new Apii();

export const useRegisterStore = defineStore('register', {
    state: () => ({
        step: 1,
        errors: [],
        formErrors: {},
        agreeToTerms: false,
        enable2fa: false,
        twoFactorType: '',
        user: {},
        sendingCode: false,
        showResendCode: true,
        resending: false,
        resendCodeMessage: 'Resending code ...',
        otpCode: '',
        qrCode: '',
        recoveryCodes: [],
        hideVerifyCodeActions: false,
        showQrCode: false,
        copied: false,
        downloaded: false,
        showCancel2faModal: false,
        registerCreds: {
            name: '',
            email: '',
            password: '',
            password_confirmation: '',
            twofa_enabled: false,
            twofa_google_enabled: false,
            twofa_agreed: false,
            pref_allow_general_marketing: true,
            phone: '',
            isMobile: false, // for testing
            // SUPPORT-729: 10xTravel+ Subscribe origin marker + billing choice.
            src: null,
            billing_interval: null,
        },
        // SUPPORT-729: destination returned by POST /register. Captured once so
        // every post-register navigation (including 2FA cancel / SMS success /
        // TOTP completion) honors the server-issued path — otherwise a
        // tenxplus buyer who enables 2FA would land on /quiz → /dashboard and
        // never reach /checkout.
        postRegisterRedirect: null,
        utmParams: {},
    }),
    getters: {},
    actions: {
        setFormError(key, message) {
            this.formErrors[key] = message;
        },
        removeFormError(key) {
            delete this.formErrors[key];
        },
        async register() {
            this.validate();

            if (this.errors.length) {
                return;
            }

            this.loading = true;
            const auth = Auth.register(this.registerCreds);
            auth.then((response) => {
                if (response.status && response.status > 201) {
                    this.handleError(response.response);
                    this.loading = false;
                    return;
                }

                // Registration logs the user into the Sanctum session; the
                // bearer token is no longer persisted to localStorage
                // (SUPPORT-663 / F-024).
                this.user = response.data;
                // SUPPORT-729: capture the server-issued redirect so 2FA
                // sub-flows can honor it too.
                this.postRegisterRedirect = response.data?.redirect || '/quiz';

                if (this.enable2fa) {
                    this.step = 2;
                    this.loading = false;
                } else {
                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        const event_id = generateUUID();
                        if (dataLayer) {
                            window.gtag('config', 'G-0RRE7P1WE9');
                            window.gtag('config', 'G-24V6GCLG87');

                            window.gtag('event', 'My10xRegistration', {
                                user_properties: {
                                    joined_my10x: 'Yes',
                                },
                            });
                        }

                        var savedHubspotEmail =
                            localStorage.getItem('hubspot_email') || '';
                        woopra.identify({
                            email: response.data.email,
                            hubspot_email: savedHubspotEmail,
                            name: response.data.name,
                            uuid: response.data.uuid,
                        });
                        woopra.track('register-my10x', {
                            name: response.data.name,
                            email: response.data.email,
                            uuid: response.data.uuid,
                            fb_event_id: event_id, // Added Facebook event_id for Woopra
                        });

                        // Track with Facebook Pixel
                        if (
                            typeof fbq !== 'undefined' &&
                            response.data.hashed_email_sha256 &&
                            response.data.uuid
                        ) {
                            fbq(
                                'track',
                                'CompleteRegistration',
                                {
                                    em: response.data.hashed_email_sha256, // Hashed email
                                    external_id: response.data.uuid, // User's UUID
                                },
                                { eventID: event_id } // Facebook's own event_id for deduplication
                            );
                        }
                    }

                    // Push to Google Analytics DataLayer
                    if (window.dataLayer) {
                        window.dataLayer.push({
                            event: 'my10x_signup_complete',
                            signup_method: 'traditional',
                            user_id: response.data.uuid,
                            user_email: response.data.email,
                        });
                    }

                    // SUPPORT-729: honor the server-issued redirect so the
                    // 10xTravel+ origin cohort lands on /checkout?billing=…
                    // instead of the default /quiz.
                    location.href = this.postRegisterRedirect || '/quiz';
                }
                this.loading = false;
            }).catch((e) => {
                this.loading = false;
                this.handleError(e.response);
            });
        },
        hash(string) {
            const utf8 = new TextEncoder().encode(string);
            return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const hashHex = hashArray
                    .map((bytes) => bytes.toString(16).padStart(2, '0'))
                    .join('');
                return hashHex;
            });
        },
        validate() {
            this.errors = [];
            if (this.passwordsMatchMessage) {
                this.errors.push({ message: 'Passwords must match.' });
            }

            if (
                this.registerCreds.twofa_enabled &&
                (this.registerCreds.phone === '' || !this.registerCreds.phone)
            ) {
                this.errors.push({
                    message: 'Please enter your phone number.',
                });
            }

            if (
                this.registerCreds.twofa_enabled &&
                !this.registerCreds.twofa_agreed
            ) {
                this.errors.push({
                    message: 'You must agree to the 2FA terms.',
                });
            }

            if (this.registerCreds.twofa_enabled) {
                if (this.registerCreds.phone.length !== 14) {
                    this.errors.push({
                        message: 'Please enter a valid phone number.',
                    });
                }
            }

            if (!this.agreeToTerms) {
                this.errors.push({
                    message:
                        'Please agree to the terms of service and privacy policy.',
                });
            }
        },
        setTwoFactorType(val) {
            this.twoFactorType = val;

            if (this.twoFactorType === 'sms') {
                this.registerCreds.twofa_enabled = true;
            } else {
                this.registerCreds.twofa_enabled = false;
            }

            this.hideVerifyCodeActions = false;
            if (this.twoFactorType === 'totp') {
                this.registerCreds.twofa_google_enabled = true;
                this.enableTotp();
            } else {
                this.registerCreds.twofa_google_enabled = false;
            }
        },
        gotoStep(step) {
            this.validateStep();
            if (this.errors.length) {
                return;
            }

            if (step === 4) {
                Auth.enableSms2fa(
                    this.registerCreds.phone,
                    this.registerCreds.password
                )
                    .then(() => {
                        this.step = step;
                    })
                    .catch((e) => {
                        this.errors.push({
                            message:
                                'There was a problem enabling 2FA. Please try again.',
                        });
                    });
            } else {
                this.step = step;
            }
        },
        cancelTwoFactor() {
            // SUPPORT-729: honor the server-issued post-register destination
            // (e.g. /checkout?billing=year for the 10xTravel+ origin cohort).
            location.href = this.postRegisterRedirect || '/quiz';
        },
        validateStep() {
            this.errors = [];
            if (
                this.step === 2 &&
                (this.twoFactorType === '' ||
                    !this.twoFactorType ||
                    this.twoFactorType.length === 0)
            ) {
                this.errors.push({ message: 'Please choose a 2FA type.' });
            }

            if (this.step === 3 && this.twoFactorType === 'sms') {
                if (!this.registerCreds.twofa_agreed) {
                    this.errors.push({
                        message: 'Please agree to the 2FA terms.',
                    });
                }

                if (this.registerCreds.phone.length < 14) {
                    this.errors.push({
                        message: 'Please enter a valid phone number.',
                    });
                }
            }
        },
        validatePhoneNumber(code) {
            this.errors = [];
            this.validateCode(code);

            if (code.toString().length === 6) {
                const auth = Auth.validatePhoneNumber({ code: code });
                auth.then((response) => {
                    if (!response.data.valid_code) {
                        this.errors.push({
                            message:
                                'The code is invalid or expired. You may resend it.',
                        });
                        this.showResendCode = true;
                        this.sendingCode = false;
                    } else {
                        // 2FA verified via the session; no token to persist
                        // (SUPPORT-663 / F-024).
                        // SUPPORT-729: honor the server-issued post-register
                        // destination.
                        location.href = this.postRegisterRedirect || '/quiz';
                    }
                }).catch((e) => {
                    this.handleError(e.response);
                    this.sendingCode = false;
                });
            }
        },
        validateCode(code) {
            if (code.toString().length !== 6) {
                this.errors.push({
                    message: 'The SMS code needs to be 6 characters.',
                });
            }
        },
        async resendSmsCode() {
            this.resending = true;
            const auth = Auth.resendSmsTwoFA(this.registerCreds.phone);
            auth.then((response) => {
                if (!response.data.success) {
                    if (response.data.message === 'please_wait') {
                        message =
                            'Too many attempts. Please wait 5 min before trying again.';
                    }
                    if (response.data.message === 'too_many_tries') {
                        message =
                            'Account is locked. Too many failed attempts.';
                    }

                    this.code = '';
                    this.showResendCode = false;
                } else {
                    this.resendCodeMessage =
                        'Code resent. Codes are valid for 5 min.';
                    this.errors = [];
                    return response;
                }
            }).catch((e) => {
                this.handleError(e.response);
            });
        },
        verifyCode(otpEl) {
            this.errors = [];
            this.loading = true;
            Auth.verify2faQrCode(this.otpCode)
                .then(() => {
                    this.hideVerifyCodeActions = true;
                    this.loading = false;
                    Auth.getRecoveryCodes()
                        .then((response) => {
                            this.recoveryCodes = response.data;
                        })
                        .catch((e) => {
                            this.errors.push({
                                message: e.response.data.message,
                            });
                        });
                })
                .catch((e) => {
                    this.errors.push({ message: e.response.data.message });
                    this.loading = false;
                    otpEl.clearInput();
                });
        },
        setOtpCode(otpCode) {
            this.otpCode = otpCode;
        },
        enableTotp() {
            this.loading = true;
            const confirmPassword = Auth.confirmPassword(
                this.registerCreds.password
            );
            confirmPassword
                .then(() => {
                    const enable2Factor = Auth.enableTwoFactor();
                    enable2Factor
                        .then(() => {
                            Auth.get2faQrCode()
                                .then((response) => {
                                    this.qrCode = response.data.svg;
                                    this.loading = false;
                                })
                                .catch((e) => {
                                    this.loading = false;
                                    this.errors.push({
                                        message: e.response.data.message,
                                    });
                                });
                        })
                        .catch((e) => {
                            this.loading = false;
                            this.errors.push({
                                message: e.response.data.message,
                            });
                        });
                })
                .catch((e) => {
                    this.loading = false;
                    this.errors.push({
                        message: e.response.data.message,
                    });
                });
        },
        async copyCodes() {
            const str = this.recoveryCodes.join('\n');

            if (
                navigator &&
                navigator.clipboard &&
                navigator.clipboard.writeText
            ) {
                this.copied = true;
                return navigator.clipboard.writeText(str);
            }
            return Promise.reject('The Clipboard API is not available.');
        },
        downloadRecoveryCodes() {
            const str = this.recoveryCodes.join('\n');
            const blob = new Blob([str], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'my10x_recovery_codes.txt';
            a.click();
            window.URL.revokeObjectURL(url);
            this.downloaded = true;
        },
        setShowCancel2faModal(val) {
            this.showCancel2faModal = val;
        },
        completeTotpSetup() {
            // SUPPORT-729: honor the server-issued post-register destination.
            location.href = this.postRegisterRedirect || '/quiz';
        },
        handleError(error) {
            if (!error) return;

            const response = normalizeError(error);
            this.formErrors = {};

            // Handle validation errors
            if (
                response.status === 422 &&
                response.data &&
                response.data.errors
            ) {
                for (const key in response.data.errors) {
                    this.errors.push({ message: response.data.errors[key][0] });
                    this.formErrors[key] = response.data.errors[key][0];
                }
            }

            // Handle authentication errors
            if (response.status === 401 || response.status === 403) {
                location.href = '/login?loginstatus=autologout';
            }
        },
        setError(message) {
            this.errors = [];
            this.errors.push({ message: message });
        },
        setUtmParams(params) {
            this.utmParams = params;
            ApiService.apiCall('/utm-params', 'post', params);
        },
        // SUPPORT-729
        setOriginContext({ src, billing }) {
            this.registerCreds.src = src ?? null;
            this.registerCreds.billing_interval =
                billing === 'month' || billing === 'year' ? billing : null;
        },
    },
});
